import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useMobileSearch } from '../../../components/MobileSearch/hooks/useMobileSearch'
import { MobileSearchDropdown } from '../../../components/MobileSearch/MobileSearchDropdown'
import { MobileSearchInput } from '../../../components/MobileSearch/MobileSearchInput'
import { MobileSearchResults } from '../../../components/MobileSearch/MobileSearchResults'
import { MobileSearchFilters } from '../../../components/MobileSearch/MobileSearchFilters'
import { MobileSearchSort } from '../../../components/MobileSearch/MobileSearchSort'
import { MobileSearchAppliedFilters } from '../../../components/MobileSearch/MobileSearchAppliedFilters'
import { BackButton } from '../../../components/MobileSearch/MobileSearch.styles'
import {
  ContactCompanySearchResult,
  DoorSearchResult,
} from '../../../models/companies'
import { SearchType } from 'components/MobileSearch/hooks/useMobileSearchAPI'

interface SearchPageProps {
  hideBack?: boolean
  onClick?: (company: ContactCompanySearchResult | DoorSearchResult) => void
  options?: { value: SearchType; label: string }[]
}

export default function SearchPage({
  hideBack = false,
  onClick,
  options,
}: SearchPageProps) {
  const navigate = useNavigate()
  const {
    state,
    setSearchType,
    setQuery,
    setFilters,
    setSort,
    removeFilter,
    clearSearch,
    loadMore,
  } = useMobileSearch()

  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isSortOpen, setIsSortOpen] = useState(false)

  const handleBack = () => {
    navigate(-1)
  }

  const handleFilterClick = () => {
    setIsSortOpen(false)
    setIsFilterOpen(true)
  }

  const handleSortClick = () => {
    setIsFilterOpen(false)
    setIsSortOpen(true)
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white">
      {/* Back Button */}
      {!hideBack && (
        <BackButton onClick={handleBack}>
          <ChevronLeft size={20} />
          <span>Back</span>
        </BackButton>
      )}

      {/* Search Controls */}
      <div className="px-2 pt-3 pb-1.5 bg-white">
        <MobileSearchDropdown
          value={state.searchType}
          label={state.searchTypeLabel}
          onChange={setSearchType}
          options={options}
        />
        <MobileSearchInput
          value={state.query}
          onChange={setQuery}
          onClear={clearSearch}
          onFilterClick={handleFilterClick}
          onSortClick={handleSortClick}
          currentSort={state.sort}
          isSortOpen={isSortOpen}
        />
        <MobileSearchAppliedFilters
          filters={state.filters}
          onRemoveFilter={removeFilter}
        />
      </div>

      {/* Results */}
      <MobileSearchResults
        results={state.results}
        total={state.total}
        query={state.query}
        searchType={state.searchType}
        isLoading={state.isLoading}
        isLoadingMore={state.isLoadingMore}
        hasMore={state.hasMore}
        error={state.error}
        onLoadMore={loadMore}
        isDebouncing={state.isDebouncing}
        onClick={onClick}
      />

      {/* Filter Panel - covers entire area between headers and footers */}
      <MobileSearchFilters
        isOpen={isFilterOpen}
        filters={state.filters}
        onClose={() => setIsFilterOpen(false)}
        onApply={setFilters}
      />

      {/* Sort Panel - covers entire area between headers and footers */}
      <MobileSearchSort
        isOpen={isSortOpen}
        currentSort={state.sort}
        onClose={() => setIsSortOpen(false)}
        onSelect={setSort}
      />
    </div>
  )
}
